<template>
  <ElDialog
    :visible="value"
    :title="$t('ADVERTISEMENT.NEGATIVE_KEYWORDS.ADD_TITLE')"
    width="40rem"
    append-to-body
    class="add-negative-keyword-from-search-terms-modal"
    @open="createModel"
    @close="closeAndReset"
  >
    <ElAlert
      v-if="showAlert"
      class="add-negative-keyword-from-search-terms-modal__alert"
      :closable="false"
      type="success"
      effect="dark"
      :title="addedKeywordMessage"
      show-icon
    />
    <ElForm
      ref="negativeKeywordForm"
      :model="negativeKeywordModelList"
      class="add-negative-keyword-from-search-terms-modal__keyword-form"
    >
      <ElFormItem
        v-for="(negativeKeywordModel, index) in negativeKeywordModelList.items"
        :key="negativeKeywordModel.id"
        :prop="'items.' + index + '.keyword'"
        :label="getLabelIfFirstInput(index)"
        :rules="negativeKeywordRule.keyword"
        class="add-negative-keyword-from-search-terms-modal__keyword-input"
      >
        <ElInput v-model="negativeKeywordModel.keyword" />
        <InputHelper
          v-if="errors[negativeKeywordModel.keyword]"
          el-icon="el-icon-warning"
          type="alert"
        >
          {{ getError(negativeKeywordModel.keyword) }}
        </InputHelper>
      </ElFormItem>
    </ElForm>
    <div slot="footer" class="dialog-footer">
      <ElButton
        type="primary"
        native-type="submit"
        size="large"
        :loading="isLoading"
        @click="addNegativeKeyword"
      >
        {{ $t('ADVERTISEMENT.NEGATIVE_KEYWORDS.ADD_BUTTON') }}
      </ElButton>
    </div>
  </ElDialog>
</template>

<script>
import form from '@/mixins/form';
import campaignId from '@/mixins/advertisement/campaignId';

import required from '@/utils/validation-rules/required';

import { NegativeKeyword } from '@/api/api';

import InputHelper from '@@/common/InputHelper';

import { AD_CAMPAIGN_SEARCH_TERM_ADD_NEGATIVE_KEYWORD_MODAL_ACTION_BUTTON } from '@/constants/gtm-events';

import {
  convertArrayOfObjectToHashTable,
  convertArrayOfPrimitiveToHashTable,
} from '@/utils/arrayToHashTable';

export default {
  name: 'AddNegativeKeywordFromSearchTermsModal',
  components: {
    InputHelper,
  },
  mixins: [form, campaignId],
  props: {
    value: {
      type: Boolean,
      required: true,
    },
    selections: {
      type: Array,
      required: true,
    },
  },
  data() {
    return {
      isLoading: false,
      addedKeywords: [],
      errors: {},
      negativeKeywordModelList: {
        items: [],
      },
    };
  },
  computed: {
    addedKeywordMessage() {
      return this.addedKeywords.length > 1
        ? this.$tc('ADVERTISEMENT.SEARCH_TERMS.ADD_NEGATIVE_BIDS_SUCCESS', 1, {
            keywords: this.addedKeywords.join(' / '),
          })
        : this.$tc('ADVERTISEMENT.SEARCH_TERMS.ADD_NEGATIVE_BIDS_SUCCESS', 0, {
            keyword: this.addedKeywords.join(' / '),
          });
    },
    showAlert() {
      return Object.keys(this.errors).length && this.addedKeywords.length;
    },
    negativeKeywordRule() {
      return {
        keyword: required(this.$t('VALIDATION_REQUIRED_ERR')),
      };
    },
  },
  methods: {
    getError(keyword) {
      return this.errors[keyword] ? this.errors[keyword].message : '';
    },
    getLabelIfFirstInput(index) {
      return index === 0
        ? this.$t('ADVERTISEMENT.NEGATIVE_KEYWORDS.KEYWORD')
        : undefined;
    },
    closeAndReset() {
      this.resetForm('negativeKeywordForm');

      this.errors = {};
      this.addedKeywords = [];

      this.$emit('input', false);
    },
    createModel() {
      this.negativeKeywordModelList = {
        items: this.selections.map(({ id, query }) => ({
          keyword: query,
          id,
        })),
      };
    },
    addNegativeKeyword() {
      if (!this.isValid('negativeKeywordForm')) return;
      this.isLoading = true;
      const negativeKeywords = this.negativeKeywordModelList.items.map(
        negativeKeywordModel => ({ keyword: negativeKeywordModel.keyword }),
      );

      this.$gtm.trackEvent(
        AD_CAMPAIGN_SEARCH_TERM_ADD_NEGATIVE_KEYWORD_MODAL_ACTION_BUTTON,
      );

      return NegativeKeyword.addBulkNegativeKeyword({
        campaignId: this.campaignId,
        negativeKeywords,
      })
        .then(({ message, data, errors }) => {
          this.errors = convertArrayOfObjectToHashTable(
            errors,
            ({ object: { keyword } }) => keyword,
          );

          this.addedKeywords = data.map(({ keyword }) => keyword);

          const addedKeywordsHashMap = convertArrayOfPrimitiveToHashTable(
            this.addedKeywords,
          );
          this.negativeKeywordModelList.items = this.negativeKeywordModelList.items.filter(
            ({ keyword }) => !(keyword in addedKeywordsHashMap),
          );

          if (!errors.length) {
            this.$message.success(message);
            this.closeAndReset();
          }
        })
        .catch(({ message }) => {
          this.$message.error(message);
        })
        .finally(() => {
          this.isLoading = false;
        });
    },
  },
};
</script>

<style lang="scss">
.add-negative-keyword-from-search-terms-modal {
  &__keyword-input {
    & + & {
      margin-top: $global-margin;
    }

    .el-form-item {
      margin-bottom: 0;
    }
  }

  &__alert {
    margin-bottom: $global-margin * 2;
  }
}
</style>
